/**
 * Token Component - Interactive Battle Map Assets
 * 
 * This component represents individual tokens (characters, monsters, maps) on the battle map.
 * It provides full interactivity including drag, drop, resize, and selection.
 * 
 * WHAT IT HANDLES:
 * - Displays token images on the canvas using react-konva
 * - Provides drag and drop functionality for moving tokens around
 * - Shows resize handles when selected (for adjusting token size)
 * - Handles right-click context menu for layer management and deletion
 * - Supports grid snapping when snap-to-grid is enabled
 * 
 * TOKEN TYPES:
 * - "player" - Player character tokens (blue border when selected)
 * - "monster" - Enemy creature tokens (red border when selected)  
 * - "npc" - Non-player character tokens (green border when selected)
 * - "map" - Background map images (no special border)
 * - "object" - Map objects like walls, doors, etc.
 * 
 * INTERACTION RULES:
 * - Only interactive when its layer is active and unlocked
 * - Map assets can only be moved/resized when Map Layer is active
 * - Character tokens can only be moved when Token Layer is active
 * - Right-click shows context menu for layer reassignment and deletion
 * 
 * INTEGRATION:
 * - Calls onDragEnd when token is moved to update position
 * - Calls onTransformEnd when token is resized to update dimensions
 * - Calls onSelect when clicked to notify BattleRoom of selection
 * - Uses grid settings from useBattleRoom for snapping calculations
 */

import React, { useEffect, useRef } from 'react';
import { Image, Transformer, Rect, Line, Text } from 'react-konva';
import useImage from 'use-image';
import { TOKEN_CONFIG, DND_CONSTANTS, getTokenBorderColor } from '../../../config/BattleRoomConstants';
import { normalizeBattleLayerId } from '../../../utils/battleRoomLayerUtils';
import { resolveCharacterArtUrl, resolveMonsterArtUrl, resolvePlaceholderUrl } from '../../../services/ArtworkResolver';
import EffectColors from '../../../config/EffectColors';

const Token = ({ token, isSelected, isPrimarySelected = false, onSelect, onChange, onDragEnd, onDragStart, onDragMove, getAssetDraggable, onContextMenu, className, onMouseEnter, onMouseLeave, targetingEnabled, isTargetSelected, onToggleTarget, gridSize, canDragToken, shouldSuppressTap, viewScale = 1, showTokenLabels = true }) => {
  // @date_of_change: 2025-09-29 - Resolve token artwork via centralized ArtworkResolver
  const primarySrc = React.useMemo(() => {
    // A BattleRoom token is the authoritative visual assignment. In particular, GMA
    // supplies generic data URIs and selected upload URLs on the token itself; resolving
    // by actor name first discards that choice and falls through to a gray placeholder.
    // date_of_change: 2026-07-11
    try {
      const explicitSrc = [token?.src, token?.tokenVisual?.url]
        .find((value) => typeof value === 'string' && value.trim());
      if (explicitSrc) return explicitSrc.trim();

      const t = (token?.type || '').toLowerCase();
      const entity = token?.entity
        || token?.entityPayload
        || token?.characterData
        || token?.monsterData
        || token;
      if (t === 'monster') return resolveMonsterArtUrl(entity);
      if (t === 'player' || t === 'character' || !t) return resolveCharacterArtUrl(entity);
      return resolveCharacterArtUrl(entity);
    } catch (e) {
      return resolvePlaceholderUrl('token-default');
    }
  }, [token]);

  // Ensure '/uploads/...' URLs are prefixed with the backend base before loading into Konva
  // Konva's useImage will immediately attempt to load the provided src; if it's relative to the
  // frontend host, it 404s. We patch this by resolving to an absolute backend URL.
  // @date_of_change: 2025-10-07 - Absolute uploads URL resolution for Konva tokens
  const [konvaSrc, setKonvaSrc] = React.useState(primarySrc);
  useEffect(() => {
    let cancelled = false;
    async function ensureAbsolute() {
      try {
        const s = primarySrc || '';
        // @date_of_change: 2025-10-09 - Prefer CDN base for '/uploads/...'
        if (typeof s === 'string' && s.startsWith('/uploads/')) {
          try {
            const cdnBase = ((typeof window !== 'undefined' && window.__artworkEnv && window.__artworkEnv.UPLOAD_BASE) || process.env.REACT_APP_UPLOAD_CDN_BASE || '').replace(/\/+$/, '');
            if (cdnBase) {
              if (!cancelled) setKonvaSrc(`${cdnBase}${s}`);
              return;
            }
          } catch (_) {}
          // Fallback: keep relative; some environments may still prefix via server
          if (!cancelled) setKonvaSrc(s);
        } else {
          setKonvaSrc(s);
        }
      } catch (_) {
        setKonvaSrc(primarySrc);
      }
    }
    ensureAbsolute();
    return () => { cancelled = true; };
  }, [primarySrc]);
  
  // Choose crossOrigin mode dynamically to avoid S3 CORS load failures while
  // still enabling canvas export when the CDN provides proper headers.
  // @date_of_change: 2025-10-03 - Fix token crossOrigin logic for assets
  const crossOriginMode = React.useMemo(() => {
    try {
      if (!konvaSrc) return undefined;
      const url = new URL(konvaSrc, window.location.href);
      const sameOrigin = url.origin === window.location.origin;
      if (sameOrigin) return undefined; // same-origin needs no CORS flag

      // Only use crossOrigin for uploaded images (uploads/), not assets
      // Assets are publicly accessible and should not use CORS
      if (konvaSrc.includes('/uploads/')) {
        return 'anonymous';
      }

      // Default: do not set crossOrigin to maximize load compatibility
      return undefined;
    } catch (e) {
      return undefined;
    }
  }, [konvaSrc]);

  // @date_of_change: 2025-10-04 - Removed spammy image source logging

  // Load primary image and backend-hosted placeholder for Konva image node
  const [image, imageStatus] = useImage(konvaSrc, crossOriginMode);
  const [fallbackImage] = useImage(resolvePlaceholderUrl('token-default'), crossOriginMode);
  
  // Only use fallback if main image explicitly failed to load
  const imageToUse = imageStatus === 'failed' ? fallbackImage : image;
  const shapeRef = useRef();
  const trRef = useRef();
  const dragButtonRef = useRef(null);
  
  // Determine if this token should be draggable based on layer restrictions AND optional token-level permission
  // @date_of_change: 2025-10-05 - Add token-level draggability gate (ownership + turn gating)
  const tokenLayerId = normalizeBattleLayerId(token.layer || 'token');
  const layerDraggable = getAssetDraggable ? getAssetDraggable(tokenLayerId) : true;
  const tokenPermission = typeof canDragToken === 'function' ? canDragToken(token) : true;
  // Targeting must be a read-only map interaction; a click-drag while selecting a target can mutate
  // positions before range validation and make an adjacent token appear out of range. date_of_change: 2026-06-23
  const isTargetingMode = !!targetingEnabled;
  const isDraggable = !isTargetingMode && layerDraggable && tokenPermission;
  
  // @date_of_change: 2025-09-07 - Use centralized border color utility
  const getBorderColor = () => getTokenBorderColor(token.type);

  // Transformer attachment with improved error handling
  useEffect(() => {
    let attachmentTimer = null;
    const transformerNode = trRef.current;
    const tokenShapeNode = shapeRef.current;

    // @date_of_change: 2025-10-08 - Attach Transformer only for primary selection
    // Group-selected tokens show border highlight, but Transformer is reserved for the primary token
    if (isPrimarySelected && !isTargetingMode) {
      // @date_of_change: 2025-09-07 - Use centralized delay constant
      // Cancel the delayed attachment when primary selection moves. Otherwise
      // an earlier token can reclaim the Transformer after the initiative panel
      // has selected a different actor. date_of_change: 2026-08-26
      attachmentTimer = setTimeout(() => {
        if (transformerNode && tokenShapeNode) {
          try {
            // Ensure the shape has an ID
            const shapeNode = tokenShapeNode;
            if (!shapeNode.id()) {
              shapeNode.id(token.id);
            }
            
            transformerNode.nodes([shapeNode]);
            
            // Force redraw of the transformer
            const layer = transformerNode.getLayer();
            if (layer) {
              layer.batchDraw();
            }
          } catch (err) {
            // Silently handle transformer errors
          }
        }
      }, 50);
    } else if (transformerNode) {
      transformerNode.nodes([]);
      if (transformerNode.getLayer()) {
        transformerNode.getLayer().batchDraw();
      }
    }

    return () => {
      if (attachmentTimer !== null) {
        clearTimeout(attachmentTimer);
      }
      if (transformerNode) {
        transformerNode.nodes([]);
        if (transformerNode.getLayer()) {
          transformerNode.getLayer().batchDraw();
        }
      }
    };
  }, [isPrimarySelected, token.id, isDraggable, isTargetingMode]);

  // Ensure token has proper position and dimensions
  // Prefer grid-based coordinates if available; fallback to pixel props for backward compatibility
  const gs = Number(gridSize) || 50; // pixels per grid cell (5 ft)
  const hasGrid = token && token.grid && Number.isFinite(token.grid.x) && Number.isFinite(token.grid.y);
  const hasSizeCells = token && token.sizeCells && Number.isFinite(token.sizeCells.w) && Number.isFinite(token.sizeCells.h);

  const derivePxRectFromGrid = () => {
    const cellW = hasSizeCells ? Math.max(1, token.sizeCells.w) : Math.max(1, Math.round((token.width || gs) / gs));
    const cellH = hasSizeCells ? Math.max(1, token.sizeCells.h) : Math.max(1, Math.round((token.height || gs) / gs));
    return {
      x: (token.grid?.x || 0) * gs,
      y: (token.grid?.y || 0) * gs,
      width: cellW * gs,
      height: cellH * gs,
    };
  };

  const pxRect = hasGrid ? derivePxRectFromGrid() : {
    x: token.x || 0,
    y: token.y || 0,
    width: token.width || gs,
    height: token.height || gs,
  };

  const tokenX = pxRect.x;
  const tokenY = pxRect.y;
  const tokenWidth = pxRect.width;
  const tokenHeight = pxRect.height;
  
  // For selection highlight
  const borderColor = getBorderColor();

  // GAP-006: Concentration badge — show purple ✦ at top-right when token is concentrating
  // rule_reference: D&D 5e 2024 concentration rules (PHB p.234) — only one at a time
  // date_of_change: 2026-05-20
  const isConcentrating = !!(token?.statusEffects?.concentrating);
  const concentrationSpell = token?.concentrationSpell ?? token?.statusEffectsMeta
    ? (() => {
        try {
          const meta = token?.statusEffectsMeta || {};
          const entry = Object.values(meta).find(m => m && m.concentration === true && m.description);
          return entry ? String(entry.description || '').replace(/\s*\(.*?\).*$/, '').trim() || null : (token?.concentrationSpell ?? null);
        } catch (_) { return null; }
      })()
    : null;

  // GAP-005: Condition badge overlay — color table for D&D 5e 2024 standard conditions
  // rule_reference: PHB 2024 conditions — each has a distinct color for rapid visual scanning
  // date_of_change: 2026-05-20
  const CONDITION_COLORS = {
    blinded:       '#555555',
    charmed:       '#E91E8C',
    deafened:      '#9E9E9E',
    frightened:    '#9C27B0',
    grappled:      '#795548',
    hidden:         '#2563EB',
    incapacitated: '#F44336',
    invisible:     '#B0BEC5',
    paralyzed:     '#FF5722',
    petrified:     '#607D8B',
    poisoned:      '#4CAF50',
    prone:         '#FF9800',
    restrained:    '#FF6F00',
    stunned:       '#2196F3',
    unconscious:   '#212121',
    exhaustion:    '#8D6E63',
  };
  const MAX_CONDITION_BADGES = 5;
  const activeConditions = React.useMemo(() => {
    const conds = token?.conditions;
    if (!Array.isArray(conds) || conds.length === 0) return [];
    return conds.map(c => String(c || '').trim()).filter(Boolean);
  }, [token]);

  // @date_of_change: 2025-10-25 - Effect rings overlay (Bless/Bane/Invisibility, etc.)
  // Visualizes ongoing effects using color-coded rings sourced from token.statusEffects
  const activeEffectKeys = React.useMemo(() => {
    try {
      const se = token && token.statusEffects ? token.statusEffects : {};
      return Object.keys(se).filter(k => !!se[k]);
    } catch (_) {
      return [];
    }
  }, [token]);
  const effectRingColors = React.useMemo(() => {
    const palette = EffectColors || {};
    const base = palette.byEffect || {};
    const table = palette || {};
    return activeEffectKeys.map(k => {
      const key = String(k || '').toLowerCase();
      const colorKey = base[key] || key;
      return table[colorKey] || '#4CAF50';
    });
  }, [activeEffectKeys]);

  // @date_of_change 2025-09-15 - KO marker support (red X overlay)
  // Automatically shows when HP reaches 0; also user can force show via token.showKoMarker
  const getHpCurrent = () => {
    if (token?.entity?.hpObj) return token.entity.hpObj.current;
    if (token?.hpObj) return token.hpObj.current;
    if (typeof token?.hp === 'number') return token.hp;
    return undefined;
  };
  const hpCurrent = getHpCurrent();
  const isZeroHp = typeof hpCurrent === 'number' && hpCurrent <= 0;
  const showKo = !!(token?.showKoMarker || isZeroHp);
  const nameplateText = String(token?.name || '').trim();
  // The viewer's explicit Labels On choice remains authoritative at every zoom.
  // Nameplates already compensate for stage scale, while Labels Off is the
  // deliberate density control for crowded encounters. date_of_change: 2026-09-13
  const showNameplate = showTokenLabels
    && token?.showName !== false
    && nameplateText.length > 0;
  const resourceBars = React.useMemo(() => {
    const bars = Array.isArray(token?.presentation?.resourceBars) ? token.presentation.resourceBars : [];
    const hp = token?.hpObj ?? token?.entity?.hpObj;
    const resources = token?.resources ?? token?.entity?.resources ?? [];
    return bars.slice(0, 3).map((bar) => {
      if (bar?.display) return { ...bar, display: bar.display };
      if (bar?.source?.kind === 'hp' && Number.isFinite(Number(hp?.current)) && Number.isFinite(Number(hp?.max))) {
        return { ...bar, display: { kind: 'exact', current: Number(hp.current), max: Number(hp.max) } };
      }
      if (bar?.source?.kind === 'temp_hp' && Number.isFinite(Number(hp?.max))) {
        return { ...bar, display: { kind: 'exact', current: Number(hp?.temp ?? 0), max: Number(hp.max) } };
      }
      const resource = resources.find((entry) => String(entry?.key ?? entry?.id) === String(bar?.source?.resourceKey));
      return Number.isFinite(Number(resource?.current)) && Number.isFinite(Number(resource?.max))
        ? { ...bar, display: { kind: 'exact', current: Number(resource.current), max: Number(resource.max) } }
        : { ...bar, display: { kind: 'unavailable' } };
    });
  }, [token]);
  const genericMarkers = Array.isArray(token?.presentation?.markers) ? token.presentation.markers.slice(0, 8) : [];

  // @date_of_change: 2025-10-04 - Removed spammy render state logging

  // Unified click handler: toggle target when targeting is armed, otherwise select
  const handleClick = (evt) => {
    if (typeof shouldSuppressTap === 'function' && shouldSuppressTap()) {
      evt.cancelBubble = true;
      try {
        evt?.evt?.preventDefault?.();
        evt?.evt?.stopPropagation?.();
      } catch (_) {}
      return;
    }
    if (targetingEnabled && typeof onToggleTarget === 'function') {
      // Targeting is mutually exclusive with inspection selection; stop both Konva and native
      // propagation so a target click cannot also select the token. date_of_change: 2026-06-23
      evt.cancelBubble = true;
      try {
        evt?.evt?.preventDefault?.();
        evt?.evt?.stopPropagation?.();
      } catch (_) {}
      try {
        onToggleTarget(token.id, evt.evt || evt);
      } catch (e) {
        // no-op
      }
      return;
    }
    if (typeof onSelect === 'function') {
      onSelect(evt);
    }
  };

  const handleContextMenuEvent = onContextMenu ? (e) => {
    try {
      e?.evt?.preventDefault?.();
      e?.evt?.stopPropagation?.();
      e.cancelBubble = true;
    } catch (_) {}

    const stage = e?.target?.getStage?.();
    const pointer = stage?.getPointerPosition?.();
    // Always use assetType=token so layer/context actions stay available consistently.
    onContextMenu(e, tokenLayerId, pointer, 'token', token.id);
    console.log('[Token] Context menu triggered with assetType: token, token.type:', token.type);
  } : undefined;

  const handlePointerDown = React.useCallback((e) => {
    if (isTargetingMode) {
      // Stop map movement without cancelling the browser/Konva click that selects
      // this target. preventDefault here suppresses click after pointer release in
      // some browsers. date_of_change: 2026-09-08
      e.cancelBubble = true;
      try {
        e?.evt?.stopPropagation?.();
      } catch (_) {}
    }
    dragButtonRef.current = e?.evt?.button ?? null;
  }, [isTargetingMode]);

  const handlePointerUp = React.useCallback((e) => {
    if (isTargetingMode) {
      e.cancelBubble = true;
      try {
        e?.evt?.stopPropagation?.();
      } catch (_) {}
    }
    dragButtonRef.current = null;
  }, [isTargetingMode]);

  const handleTokenDragStart = isDraggable ? (e) => {
    if (dragButtonRef.current === 1) {
      e?.target?.stopDrag?.();
      return;
    }

    if (typeof onDragStart === 'function') {
      onDragStart(e);
    }
  } : undefined;

  const handleTokenDragEnd = isDraggable ? (e) => {
    dragButtonRef.current = null;

    const newPos = {
      x: isNaN(e.target.x()) ? tokenX : e.target.x(),
      y: isNaN(e.target.y()) ? tokenY : e.target.y()
    };
    const newGrid = gs > 0 ? {
      x: Math.round(newPos.x / gs),
      y: Math.round(newPos.y / gs)
    } : undefined;

    if (typeof onDragEnd === 'function') {
      onDragEnd(e, newPos, newGrid);
    }
  } : undefined;

  return (
    <React.Fragment>
      {/* Transparent hit box so token selection works reliably even when artwork has transparent pixels. */}
      <Rect
        id={token.id}
        x={tokenX}
        y={tokenY}
        width={tokenWidth}
        height={tokenHeight}
        fill="rgba(0,0,0,0.01)"
        listening={true}
        perfectDrawEnabled={false}
        onMouseDown={handlePointerDown}
        onMouseUp={handlePointerUp}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={onMouseEnter ? () => onMouseEnter(token.id) : undefined}
        onMouseLeave={onMouseLeave ? () => onMouseLeave() : undefined}
        onClick={handleClick}
        onTap={handleClick}
        onContextMenu={handleContextMenuEvent}
      />

      {/* Token image - core component with fallback for invisible token fix */}
      <Image
        ref={shapeRef}
        image={imageToUse}
        x={tokenX}
        y={tokenY}
        width={tokenWidth}
        height={tokenHeight}
        draggable={isDraggable}
        id={token.id}
        listening={true}
        onMouseDown={handlePointerDown}
        onMouseUp={handlePointerUp}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={onMouseEnter ? () => onMouseEnter(token.id) : undefined}
        onMouseLeave={onMouseLeave ? () => onMouseLeave() : undefined}
        onClick={handleClick}
        onTap={handleClick}
        // @date_of_change: 2025-10-08 - Forward drag move for group dragging visuals
        onDragMove={isDraggable && typeof onDragMove === 'function' ? onDragMove : undefined}
        onError={() => {
          // Log image loading errors for debugging
          console.warn(`[Token ${token.id}] Image failed to load:`, {
            tokenSrc: token.src,
            primarySrc,
            imageStatus,
            crossOriginMode
          });
        }}
        onDragEnd={handleTokenDragEnd}
        onContextMenu={handleContextMenuEvent}
        onDragStart={handleTokenDragStart}
        onTransformEnd={isDraggable ? (e) => {
          // Get shape and transformer details
          const node = shapeRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          
          // Reset scale to 1 and adjust width/height
          node.scaleX(1);
          node.scaleY(1);
          // @date_of_change: 2025-09-07 - Use centralized minimum token size
          const newWidth = Math.max(DND_CONSTANTS.MIN_TOKEN_SIZE, node.width() * scaleX);
          const newHeight = Math.max(DND_CONSTANTS.MIN_TOKEN_SIZE, node.height() * scaleY);
          
          // Calculate new position after transform
          const newX = node.x();
          const newY = node.y();
          
          // Prevent infinite onChange loop by checking if values actually changed
          if (
            Math.abs(newX - tokenX) > 0.1 || 
            Math.abs(newY - tokenY) > 0.1 || 
            Math.abs(newWidth - tokenWidth) > 0.1 || 
            Math.abs(newHeight - tokenHeight) > 0.1
          ) {
            // Also compute derived grid + sizeCells for persistence
            const derivedGrid = gs > 0 ? { x: Math.round(newX / gs), y: Math.round(newY / gs) } : undefined;
            const derivedSizeCells = gs > 0 ? { w: Math.max(1, Math.round(newWidth / gs)), h: Math.max(1, Math.round(newHeight / gs)) } : undefined;
            onChange({
              ...token,
              x: newX,
              y: newY,
              width: newWidth,
              height: newHeight,
              ...(derivedGrid ? { grid: derivedGrid } : {}),
              ...(derivedSizeCells ? { sizeCells: derivedSizeCells } : {}),
            });
          }
        } : undefined}
      />

      {/* Effect rings overlay */}
      {effectRingColors && effectRingColors.length > 0 && (
        <React.Fragment>
          {effectRingColors.slice(0, 4).map((strokeColor, idx) => {
            const pad = 2 + idx * 3;
            return (
              <Rect
                key={`effring-${token.id}-${idx}`}
                x={tokenX - pad}
                y={tokenY - pad}
                width={tokenWidth + pad * 2}
                height={tokenHeight + pad * 2}
                fill="transparent"
                stroke={strokeColor}
                strokeWidth={2}
                cornerRadius={3}
                perfectDrawEnabled={false}
                listening={false}
              />
            );
          })}
        </React.Fragment>
      )}

      {/* GAP-006: Concentration badge — top-right of token
       * Purple ✦ badge (+ abbreviated spell name if available) while concentrating.
       * rule_reference: D&D 5e 2024 concentration (PHB p.234)
       * date_of_change: 2026-05-20
       */}
      {isConcentrating && (() => {
        const badgeH = Math.max(12, Math.min(16, Math.floor(tokenHeight / 6)));
        const label = concentrationSpell
          ? `✦ ${String(concentrationSpell).slice(0, 8)}`
          : '✦ C';
        const badgeW = Math.max(16, badgeH * label.length * 0.55);
        return (
          <React.Fragment>
            <Rect
              x={tokenX + tokenWidth - badgeW - 1}
              y={tokenY + 1}
              width={badgeW}
              height={badgeH}
              fill="#6a1b9a"
              cornerRadius={2}
              opacity={0.92}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Text
              x={tokenX + tokenWidth - badgeW - 1}
              y={tokenY + 2}
              width={badgeW}
              height={badgeH}
              text={label}
              fontSize={Math.max(7, badgeH - 4)}
              fontStyle="bold"
              fill="#ffffff"
              align="center"
              verticalAlign="middle"
              listening={false}
              perfectDrawEnabled={false}
            />
          </React.Fragment>
        );
      })()}

      {/* GAP-005: Condition badge overlay — bottom-right corner of token
       * Each badge is a small colored Rect with a 2-letter abbreviation.
       * Max MAX_CONDITION_BADGES shown; overflow shown as "+N" badge.
       * rule_reference: D&D 5e 2024 conditions (PHB pp. 367-371)
       * date_of_change: 2026-05-20
       */}
      {activeConditions.length > 0 && (() => {
        const badgeSize = Math.max(12, Math.min(18, Math.floor(tokenWidth / 5)));
        const gap = 2;
        const visibleConds = activeConditions.slice(0, MAX_CONDITION_BADGES);
        const overflowCount = activeConditions.length - visibleConds.length;
        const totalBadges = visibleConds.length + (overflowCount > 0 ? 1 : 0);
        // Stack badges right-to-left from the bottom-right corner
        const startX = tokenX + tokenWidth - (badgeSize + gap) * totalBadges;
        const badgeY = tokenY + tokenHeight - badgeSize - 2;
        return (
          <React.Fragment>
            {visibleConds.map((cond, idx) => {
              const key = cond.toLowerCase();
              const color = CONDITION_COLORS[key] || '#888888';
              const abbr = cond.slice(0, 2).toUpperCase();
              const bx = startX + idx * (badgeSize + gap);
              return (
                <React.Fragment key={`cond-${token.id}-${key}`}>
                  <Rect
                    x={bx}
                    y={badgeY}
                    width={badgeSize}
                    height={badgeSize}
                    fill={color}
                    cornerRadius={2}
                    opacity={0.92}
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                  <Text
                    x={bx}
                    y={badgeY + 1}
                    width={badgeSize}
                    height={badgeSize}
                    text={abbr}
                    fontSize={Math.max(7, badgeSize - 5)}
                    fontStyle="bold"
                    fill="#ffffff"
                    align="center"
                    verticalAlign="middle"
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                </React.Fragment>
              );
            })}
            {overflowCount > 0 && (
              <React.Fragment>
                <Rect
                  x={startX + visibleConds.length * (badgeSize + gap)}
                  y={badgeY}
                  width={badgeSize}
                  height={badgeSize}
                  fill="#444444"
                  cornerRadius={2}
                  opacity={0.92}
                  listening={false}
                  perfectDrawEnabled={false}
                />
                <Text
                  x={startX + visibleConds.length * (badgeSize + gap)}
                  y={badgeY + 1}
                  width={badgeSize}
                  height={badgeSize}
                  text={`+${overflowCount}`}
                  fontSize={Math.max(7, badgeSize - 6)}
                  fontStyle="bold"
                  fill="#ffffff"
                  align="center"
                  verticalAlign="middle"
                  listening={false}
                  perfectDrawEnabled={false}
                />
              </React.Fragment>
            )}
          </React.Fragment>
        );
      })()}

      {/* Server-projected resource bars remain screen-readable across zoom and
          never infer values absent from the projection. date_of_change: 2026-08-15 */}
      {resourceBars.length > 0 && (() => {
        const readableScale = Math.max(0.2, Math.min(5, Number(viewScale) || 1));
        const barHeight = 6 / readableScale;
        const gap = 2 / readableScale;
        const barWidth = Math.max(tokenWidth, 54 / readableScale);
        const barX = tokenX + (tokenWidth - barWidth) / 2;
        const colors = { hp: '#c7504f', temp_hp: '#50a6c7', primary: '#d0b07a', secondary: '#8f7ac7', warning: '#d98a42' };
        return resourceBars.map((bar, index) => {
          const current = Number(bar?.display?.current);
          const max = Number(bar?.display?.max);
          const percent = bar?.display?.kind === 'band'
            ? Number(bar.display.percent)
            : (Number.isFinite(current) && Number.isFinite(max) && max > 0 ? Math.max(0, Math.min(100, current / max * 100)) : 0);
          const label = bar?.display?.kind === 'band'
            ? `${bar.label}: ${bar.display.label}`
            : bar?.display?.kind === 'exact' ? `${bar.label}: ${current}/${max}` : `${bar.label}: unavailable`;
          const y = tokenY + tokenHeight + (4 / readableScale) + index * (barHeight + gap);
          return (
            <React.Fragment key={`bar-${token.id}-${bar.id}`}>
              <Rect x={barX} y={y} width={barWidth} height={barHeight} fill="rgba(12,16,22,0.95)" stroke="#f3ead7" strokeWidth={0.6 / readableScale} cornerRadius={2 / readableScale} listening={false} />
              <Rect x={barX} y={y} width={barWidth * percent / 100} height={barHeight} fill={colors[bar.colorToken] || colors.primary} cornerRadius={2 / readableScale} listening={false} />
              <Text x={barX} y={y - (10 / readableScale)} width={barWidth} height={10 / readableScale} text={label} fontSize={7 / readableScale} fill="#f3ead7" align="center" listening={false} />
            </React.Fragment>
          );
        });
      })()}

      {/* Generic markers are presentation only and do not join the structured
          condition/effect arrays. date_of_change: 2026-08-15 */}
      {genericMarkers.length > 0 && (() => {
        const readableScale = Math.max(0.2, Math.min(5, Number(viewScale) || 1));
        const size = 16 / readableScale;
        const colors = { red: '#c7504f', amber: '#d98a42', green: '#4f9b69', blue: '#4f82b8', violet: '#8f6fc2', white: '#eee8da' };
        const symbols = { dot: '●', star: '★', skull: '☠', shield: '◆', eye: '◉', flag: '⚑' };
        return genericMarkers.map((marker, index) => {
          const x = tokenX + (index % 4) * (size + 2 / readableScale);
          const y = tokenY - size - (Math.floor(index / 4) * (size + 2 / readableScale));
          const text = marker.symbolKey === 'number' ? String(marker.number ?? '•') : (symbols[marker.symbolKey] || '●');
          return (
            <React.Fragment key={`marker-${token.id}-${marker.markerId}`}>
              <Rect x={x} y={y} width={size} height={size} fill={colors[marker.colorToken] || colors.white} stroke="#111722" strokeWidth={1 / readableScale} cornerRadius={size / 2} listening={false} />
              <Text x={x} y={y + (1 / readableScale)} width={size} height={size} text={text} fontSize={10 / readableScale} fontStyle="bold" fill={marker.colorToken === 'white' ? '#111722' : '#ffffff'} align="center" verticalAlign="middle" listening={false} />
            </React.Fragment>
          );
        });
      })()}

      {/* The existing showName property now controls an actual map nameplate.
          It is presentation-only; hiding the label does not hide or rename the
          token record. date_of_change: 2026-08-11 */}
      {showNameplate && (() => {
        const label = nameplateText.length > 32 ? `${nameplateText.slice(0, 31)}…` : nameplateText;
        const readableScale = Math.max(0.2, Math.min(5, Number(viewScale) || 1));
        const nameplateHeight = 18 / readableScale;
        const nameplateScreenWidth = Math.max(
          Math.min(tokenWidth * readableScale, 72),
          Math.min(180, Math.max(72, label.length * 6.5 + 18))
        );
        const nameplateWidth = nameplateScreenWidth / readableScale;
        const nameplateX = tokenX + (tokenWidth - nameplateWidth) / 2;
        const resourceOffset = resourceBars.length * (8 / readableScale) + (resourceBars.length ? 8 / readableScale : 0);
        const nameplateY = tokenY + tokenHeight + (4 / readableScale) + resourceOffset;
        return (
          <React.Fragment>
            <Rect
              x={nameplateX}
              y={nameplateY}
              width={nameplateWidth}
              height={nameplateHeight}
              fill="rgba(15, 22, 35, 0.94)"
              stroke="#d0b07a"
              strokeWidth={1 / readableScale}
              cornerRadius={4 / readableScale}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Text
              x={nameplateX + (5 / readableScale)}
              y={nameplateY + (2 / readableScale)}
              width={nameplateWidth - (10 / readableScale)}
              height={nameplateHeight - (4 / readableScale)}
              text={label}
              fontFamily="Inter, sans-serif"
              fontSize={11 / readableScale}
              fontStyle="bold"
              fill="#f3ead7"
              align="center"
              verticalAlign="middle"
              ellipsis
              listening={false}
              perfectDrawEnabled={false}
            />
          </React.Fragment>
        );
      })()}

      {/* KO marker (red X) overlay - renders when HP is 0 or when user toggles showKoMarker */}
      {showKo && (
        <React.Fragment>
          <Line
            points={[tokenX, tokenY, tokenX + tokenWidth, tokenY + tokenHeight]}
            stroke="rgba(220,0,0,0.9)"
            strokeWidth={Math.max(4, Math.round(Math.min(tokenWidth, tokenHeight) * 0.06))}
            listening={false}
            perfectDrawEnabled={false}
          />
          <Line
            points={[tokenX + tokenWidth, tokenY, tokenX, tokenY + tokenHeight]}
            stroke="rgba(220,0,0,0.9)"
            strokeWidth={Math.max(4, Math.round(Math.min(tokenWidth, tokenHeight) * 0.06))}
            listening={false}
            perfectDrawEnabled={false}
          />
        </React.Fragment>
      )}

      {/* Targeting visuals - show when targeting is armed and this token is selected as a target */}
      {targetingEnabled && isTargetSelected && (
        <Rect
          x={tokenX - 3}
          y={tokenY - 3}
          width={tokenWidth + 6}
          height={tokenHeight + 6}
          fill="transparent"
          stroke={TOKEN_CONFIG.TARGET_HIGHLIGHT_COLOR}
          strokeWidth={TOKEN_CONFIG.TARGET_BORDER_WIDTH}
          cornerRadius={3}
          perfectDrawEnabled={false}
          listening={false}
          // Removed zIndex - not needed as elements render in order
        />
      )}

      {/* Selection visuals - border for any selected token (including group) */}
      {isSelected && (
        <Rect
          x={tokenX - 2}
          y={tokenY - 2}
          width={tokenWidth + 4}
          height={tokenHeight + 4}
          fill="transparent"
          stroke={borderColor}
          strokeWidth={TOKEN_CONFIG.SELECTION_BORDER_WIDTH}
          cornerRadius={2}
          perfectDrawEnabled={false}
          listening={false}
        />
      )}

      {/* Transformer - only for the primary selected token */}
      {isPrimarySelected && !isTargetingMode && (
        <Transformer
          ref={trRef}
          borderDash={[5, 2]}
          borderStroke={borderColor}
          anchorStroke={borderColor}
          anchorFill="#ffffff"
          anchorSize={8}
          // Only allow resizing if the token is in an interactive layer
          resizeEnabled={isDraggable}
          rotateEnabled={isDraggable}
          enabledAnchors={isDraggable ? ['top-left', 'top-right', 'bottom-left', 'bottom-right'] : []}
          boundBoxFunc={(oldBox, newBox) => {
            // @date_of_change: 2025-09-07 - Use centralized minimum token size
            if (newBox.width < DND_CONSTANTS.MIN_TOKEN_SIZE || newBox.height < DND_CONSTANTS.MIN_TOKEN_SIZE) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </React.Fragment>
  );
};

// Custom comparison function for React.memo
const areEqual = (prevProps, nextProps) => {
  // These are the props we want to compare deeply
  const {
    token: prevToken,
    isSelected: prevSelected,
    isPrimarySelected: prevPrimarySelected,
    targetingEnabled: prevTargeting,
    isTargetSelected: prevIsTarget
  } = prevProps;
  const {
    token: nextToken,
    isSelected: nextSelected,
    isPrimarySelected: nextPrimarySelected,
    targetingEnabled: nextTargeting,
    isTargetSelected: nextIsTarget
  } = nextProps;
  
  // Compare token properties that affect rendering
  const tokenEqual = 
    prevToken.id === nextToken.id &&
    prevToken.x === nextToken.x &&
    prevToken.y === nextToken.y &&
    prevToken.width === nextToken.width &&
    prevToken.height === nextToken.height &&
    prevToken.name === nextToken.name &&
    prevToken.showName === nextToken.showName &&
    prevToken.src === nextToken.src &&
    prevToken.layer === nextToken.layer &&
    prevToken.type === nextToken.type &&
    JSON.stringify(prevToken.statusEffects || {}) === JSON.stringify(nextToken.statusEffects || {}) &&
    JSON.stringify(prevToken.statusEffectsMeta || {}) === JSON.stringify(nextToken.statusEffectsMeta || {}) &&
    JSON.stringify(prevToken.presentation || {}) === JSON.stringify(nextToken.presentation || {}) &&
    JSON.stringify(prevToken.hpObj || prevToken.entity?.hpObj || {}) === JSON.stringify(nextToken.hpObj || nextToken.entity?.hpObj || {}) &&
    JSON.stringify(prevToken.resources || prevToken.entity?.resources || []) === JSON.stringify(nextToken.resources || nextToken.entity?.resources || []) &&
    // GAP-005: re-render when conditions array changes — date_of_change: 2026-05-20
    JSON.stringify(prevToken.conditions || []) === JSON.stringify(nextToken.conditions || []) &&
    // GAP-006: re-render when concentration state changes — date_of_change: 2026-05-20
    !!(prevToken.statusEffects?.concentrating) === !!(nextToken.statusEffects?.concentrating) &&
    (prevToken.concentrationSpell ?? null) === (nextToken.concentrationSpell ?? null);
  
  // CRITICAL: Only compare selection state, do NOT compare handler references
  // This ensures that click events always pass through for proper selection
  // Also compare targeting flags so highlighting updates correctly
  // Primary selection controls the sole Transformer. It is independent from
  // group highlighting and must invalidate the memoized token when it moves.
  // date_of_change: 2026-08-26
  return tokenEqual
    && prevSelected === nextSelected
    && prevPrimarySelected === nextPrimarySelected
    && prevTargeting === nextTargeting
    && !!prevIsTarget === !!nextIsTarget
    // Viewer-local presentation changes must invalidate the Konva node even
    // when authoritative token data is unchanged. date_of_change: 2026-09-13
    && prevProps.showTokenLabels === nextProps.showTokenLabels
    && prevProps.viewScale === nextProps.viewScale;
  // We deliberately do NOT compare handler functions to ensure events always work
};

// Export memoized version of Token component
export default React.memo(Token, areEqual);
